// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { fmt, EducBox } from './shared';
import { useAuth } from '../../App';

// Deployment 25 — Sezione E: FAQ schema.org per rich snippet Google.
// react-helmet 6.x non inietta correttamente <script type="application/ld+json"> con
// React 18, quindi gestiamo l'inserimento via document.head.appendChild in un useEffect.
const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Quanto si paga di imposta di successione tra coniugi e figli in Italia?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tra coniugi e figli l'aliquota è del 4% ma si applica solo sull'importo che supera €1.000.000 per ogni erede. La maggior parte delle successioni familiari non paga alcuna imposta di successione."
      }
    },
    {
      "@type": "Question",
      "name": "Cosa è la quota di legittima nella successione italiana?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "La quota di legittima è la parte di patrimonio riservata per legge a coniuge, figli e genitori, indipendentemente dal testamento. Con coniuge e 2 figli, la quota disponibile liberamente è solo il 25% del patrimonio totale."
      }
    },
    {
      "@type": "Question",
      "name": "Conviene donare il patrimonio in vita invece di lasciarlo in eredità?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "In alcuni casi sì: la donazione sconta le stesse aliquote e franchigie della successione, ma il patrimonio donato oggi non crescerà ulteriormente nella base imponibile futura. Conviene valutare con un notaio la situazione specifica."
      }
    }
  ]
};

// Local ResultCard for this calculator
const ResultCard = ({ label, value, color = '#E8E8E4', highlight = false, sub }) => (
  <div className={`p-4 rounded-xl border ${highlight ? 'border-[rgba(201,168,76,0.2)]' : 'border-white/[0.06]'}`}
    style={{ background: highlight ? 'rgba(201,168,76,0.04)' : '#252523' }}>
    <p className="text-xs text-[#888780] mb-1">{label}</p>
    <p className="text-xl font-semibold" style={{ color }}>{value}</p>
    {sub && <p className="text-xs text-[#888780] mt-1">{sub}</p>}
  </div>
);

// Local InputRow
const InputRow = ({ label, value, onChange, prefix, type = 'number' }) => (
  <div>
    <Label className="text-[#888780] text-sm">{label}</Label>
    <div className="relative mt-1">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-sm">{prefix}</span>}
      <Input type={type} value={value} onChange={e => onChange(e.target.value)}
        className={`bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] ${prefix ? 'pl-7' : ''}`} />
    </div>
  </div>
);

const CATEGORIE = [
  { id: 'coniuge_figli', label: 'Coniuge / Figli / Genitori', aliquota: 0.04, franchigia: 1000000 },
  { id: 'fratelli', label: 'Fratelli / Sorelle', aliquota: 0.06, franchigia: 100000 },
  { id: 'altri_parenti', label: 'Altri parenti fino al 4° grado', aliquota: 0.06, franchigia: 0 },
  { id: 'altri', label: 'Altri soggetti / non parenti', aliquota: 0.08, franchigia: 0 },
  { id: 'disabile', label: 'Portatore di handicap grave', aliquota: 0.04, franchigia: 1500000 },
];

// Deployment 24 — Sezione D / Modulo 2: quote di legittima (art. 536-564 c.c.)
const calcolaQuoteLegittima = (numFigli, haConiuge) => {
  if (numFigli === 0 && !haConiuge) {
    return { legittima: 1 / 3, legittimaAscendenti: 1 / 3, disponibile: 2 / 3, note: 'Solo genitori/ascendenti: 1/3 legittima, 2/3 disponibile' };
  }
  if (numFigli === 0 && haConiuge) {
    return { legittimaConiuge: 1 / 2, disponibile: 1 / 2 };
  }
  if (numFigli === 1 && !haConiuge) {
    return { legittimaFigli: 1 / 2, disponibile: 1 / 2 };
  }
  if (numFigli >= 2 && !haConiuge) {
    return { legittimaFigli: 2 / 3, disponibile: 1 / 3 };
  }
  if (numFigli === 1 && haConiuge) {
    return { legittimaConiuge: 1 / 4, legittimaFigli: 1 / 4, disponibile: 1 / 2 };
  }
  // numFigli >= 2 && haConiuge
  return { legittimaConiuge: 1 / 4, legittimaFigli: 1 / 2, disponibile: 1 / 4 };
};

// ── Shared accordion section ────────────────────────────────────────────────
const AccordionSection = ({ title, subtitle, isOpen, onToggle, premium, locked, children, testid }) => (
  <div
    className="rounded-xl border mt-4"
    style={{ background: '#252523', borderColor: 'rgba(255,255,255,0.06)' }}
    data-testid={testid}
  >
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 text-left"
      aria-expanded={isOpen}
    >
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-[#E8E8E4]">{title}</span>
        {premium && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: '#C9A84C', color: '#3A2800' }}>
            Premium
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {subtitle && <span className="text-xs text-[#888780] hidden sm:inline">{subtitle}</span>}
        {isOpen ? <ChevronDown className="w-4 h-4 text-[#888780]" /> : <ChevronRight className="w-4 h-4 text-[#888780]" />}
      </div>
    </button>
    {isOpen && (
      <div className="px-4 pb-4 pt-1 relative" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        {locked && (
          <div
            className="absolute inset-0 rounded-b-xl flex flex-col items-center justify-center gap-2"
            style={{ background: 'rgba(28,28,26,0.92)', backdropFilter: 'blur(8px)', zIndex: 10 }}
            data-testid={`${testid}-lock`}
          >
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Lock className="w-5 h-5 text-[#C9A84C]" />
            </div>
            <p className="text-[#E8E8E4] font-semibold">Modulo Premium</p>
            <p className="text-xs text-[#888780] text-center max-w-[280px]">
              Passa a Premium per accedere al confronto donazione vs successione
            </p>
          </div>
        )}
        {children}
      </div>
    )}
  </div>
);

const ImpostaSuccessioneCalc = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPreviewPremium = typeof window !== 'undefined' && sessionStorage.getItem('admin_preview_premium') === 'true';
  const isPremium = isPreviewPremium || (user && user.tier === 'premium');

  // Deployment 25 — Sezione E: inietta JSON-LD FAQPage in <head> al mount
  useEffect(() => {
    const id = 'sapiens-faq-successione';
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(FAQ_JSON_LD);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  // ── Modulo 1 state (invariato) ────────────────────────────────────────
  const [valoreEredita, setValoreEredita] = useState('300000');
  const [numEredi, setNumEredi] = useState('2');
  const [categoria, setCategoria] = useState('coniuge_figli');
  const [includiBtp, setIncludiBtp] = useState(false);
  const [valoreBtp, setValoreBtp] = useState('0');

  // ── Modulo 2 state ────────────────────────────────────────────────────
  const [numFigli, setNumFigli] = useState('2');
  const [haConiuge, setHaConiuge] = useState(true);

  // ── Modulo 3 state — composizione patrimoniale (slider 0-100) ─────────
  const [percImmobili, setPercImmobili] = useState(50);
  const [percLiquidita, setPercLiquidita] = useState(30);
  const [percInvestimenti, setPercInvestimenti] = useState(20);

  // ── Modulo 4 state — donazione vs successione ────────────────────────
  const [anniAllaSuccessione, setAnniAllaSuccessione] = useState(15);
  const [tassoCrescita, setTassoCrescita] = useState('3');

  // Accordion state — Modulo 1 sempre visibile; 2-3-4 accordion progressivo
  const [open2, setOpen2] = useState(false);
  const [open3, setOpen3] = useState(false);
  const [open4, setOpen4] = useState(false);

  // ── Modulo 1 calcoli (invariato) ──────────────────────────────────────
  const cat = CATEGORIE.find(c => c.id === categoria);
  const V = parseFloat(valoreEredita) || 0;
  const N = Math.max(1, parseInt(numEredi) || 1);
  const btpVal = includiBtp ? (parseFloat(valoreBtp) || 0) : 0;
  const baseImponibile = Math.max(0, V - btpVal);
  const quotaPerErede = baseImponibile / N;
  const baseImponibileDopoFranchigia = Math.max(0, quotaPerErede - cat.franchigia);
  const impostaSingoloErede = baseImponibileDopoFranchigia * cat.aliquota;
  const impostaTotale = impostaSingoloErede * N;
  const aliquotaEffettiva = baseImponibile > 0 ? (impostaTotale / baseImponibile) * 100 : 0;
  const nettoErede = quotaPerErede - impostaSingoloErede;

  // Helper: calcola imposta totale dato patrimonio (base imponibile pre-BTP), grado parentela e numero eredi
  const calcolaImposta = (patrimonio, catObj, nEredi) => {
    const quota = patrimonio / Math.max(1, nEredi);
    const dopoFranch = Math.max(0, quota - catObj.franchigia);
    return dopoFranch * catObj.aliquota * Math.max(1, nEredi);
  };

  // ── Modulo 2 calcoli ──────────────────────────────────────────────────
  const nFigli = parseInt(numFigli, 10) || 0;
  const quote = useMemo(() => calcolaQuoteLegittima(nFigli, haConiuge), [nFigli, haConiuge]);
  const quoteInEuro = useMemo(() => {
    const out = { disponibile: V * quote.disponibile };
    if (quote.legittimaConiuge != null) out.legittimaConiuge = V * quote.legittimaConiuge;
    if (quote.legittimaFigli != null) {
      out.legittimaFigli = V * quote.legittimaFigli;
      out.perFiglio = nFigli > 0 ? out.legittimaFigli / nFigli : 0;
    }
    if (quote.legittima != null) out.legittima = V * quote.legittima;
    if (quote.legittimaAscendenti != null) out.legittimaAscendenti = V * quote.legittimaAscendenti;
    return out;
  }, [quote, V, nFigli]);

  // ── Modulo 3 calcoli — composizione normalizzata al 100% ──────────────
  const sumPerc = percImmobili + percLiquidita + percInvestimenti;
  const norm = sumPerc > 0 ? sumPerc : 100;
  const pImm = (percImmobili / norm) * 100;
  const pLiq = (percLiquidita / norm) * 100;
  const pInv = (percInvestimenti / norm) * 100;
  const needsNormalization = Math.abs(sumPerc - 100) > 0.5;

  const valoreFiscaleImmobili = V * (pImm / 100) * 0.70;
  const valoreAltri = V * ((pLiq + pInv) / 100);
  const patrimonioFiscale = valoreFiscaleImmobili + valoreAltri;

  const eredi = useMemo(() => {
    const rows = [];
    const valoreLordoPerErede = V / N;
    const fiscalePerErede = patrimonioFiscale / N;
    const imponibile = Math.max(0, fiscalePerErede - cat.franchigia);
    const imposte = imponibile * cat.aliquota;
    const netto = valoreLordoPerErede - imposte;
    for (let i = 1; i <= N; i++) {
      rows.push({ idx: i, lordo: valoreLordoPerErede, imposte, netto });
    }
    return rows;
  }, [V, N, patrimonioFiscale, cat]);

  // ── Modulo 4 calcoli ──────────────────────────────────────────────────
  const tassoNum = (parseFloat(tassoCrescita) || 0) / 100;
  const anniNum = Math.max(1, Math.min(30, parseInt(anniAllaSuccessione, 10) || 1));
  const patrimonioFuturo = V * Math.pow(1 + tassoNum, anniNum);
  const impostaSuccessioneFutura = calcolaImposta(patrimonioFuturo, cat, N);
  const impostaDonazioneOra = calcolaImposta(V, cat, N);
  const risparmioFiscale = impostaSuccessioneFutura - impostaDonazioneOra;
  const risparmioPercentuale = impostaSuccessioneFutura > 0
    ? (risparmioFiscale / impostaSuccessioneFutura) * 100
    : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="successione-calc">
      <EducBox>
        <strong style={{ color: '#C9A84C' }}>Imposta di successione in Italia</strong>: si applica al trasferimento di beni per eredità. Le aliquote variano dal 4% al 8% a seconda del grado di parentela, con franchigie che azzerano l'imposta per patrimoni sotto soglia.
      </EducBox>

      {/* ── MODULO 1 — Imposta base (già esistente) ───────────────────── */}
      <div data-testid="successione-modulo-1">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(61,122,82,0.15)', color: '#3D7A52' }}>Modulo 1</span>
          <h3 className="text-sm font-semibold text-[#E8E8E4]">Imposta di successione</h3>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <InputRow label="Valore totale dell'eredità (€)" value={valoreEredita} onChange={setValoreEredita} prefix="€" />
          <InputRow label="Numero di eredi" value={numEredi} onChange={setNumEredi} />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-[#E8E8E4] mb-2">Grado di parentela degli eredi</label>
          <div className="grid sm:grid-cols-2 gap-2">
            {CATEGORIE.map(c => (
              <button
                key={c.id}
                onClick={() => setCategoria(c.id)}
                className={`px-4 py-3 rounded-lg text-sm text-left transition-all ${categoria === c.id ? 'text-[#3A2800] font-medium' : 'text-[#888780] border border-white/10'}`}
                style={{ background: categoria === c.id ? '#C9A84C' : '#252523' }}
              >
                <div className="font-medium">{c.label}</div>
                <div className={`text-xs mt-0.5 ${categoria === c.id ? 'text-[#3A2800]/70' : 'text-[#555553]'}`}>
                  {c.aliquota * 100}% · Franchigia {c.franchigia > 0 ? `€${(c.franchigia / 1000).toFixed(0)}k/erede` : 'nessuna'}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6 p-4 rounded-xl border" style={{ background: 'rgba(61,122,82,0.06)', borderColor: 'rgba(61,122,82,0.2)' }}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={includiBtp} onChange={e => setIncludiBtp(e.target.checked)} className="w-4 h-4 rounded" />
            <span className="text-sm text-[#E8E8E4]">L'eredità include BTP / titoli di Stato italiani (esenti da imposta)</span>
          </label>
          {includiBtp && (
            <div className="mt-3">
              <InputRow label="Valore titoli di Stato esenti (€)" value={valoreBtp} onChange={setValoreBtp} prefix="€" />
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <ResultCard label="Imposta totale" value={`€${fmt(impostaTotale)}`} color="#E87040" highlight={impostaTotale > 0} />
          <ResultCard label="Imposta per erede" value={`€${fmt(impostaSingoloErede)}`} color="#C9A84C" />
          <ResultCard label="Netto per erede" value={`€${fmt(nettoErede)}`} color="#3D7A52" highlight />
          <ResultCard label="Quota per erede (lordo)" value={`€${fmt(quotaPerErede)}`} color="#5B8DB8" />
          <ResultCard label="Aliquota effettiva" value={`${aliquotaEffettiva.toFixed(2)}%`} color="#888780" />
          <ResultCard label="Base imponibile" value={`€${fmt(baseImponibile)}`} color="#888780" />
        </div>

        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 text-[#888780] font-medium">Voce</th>
                <th className="text-right py-2 text-[#888780] font-medium">Per erede</th>
                <th className="text-right py-2 text-[#888780] font-medium">Totale {N} eredi</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/[0.04]">
                <td className="py-2.5 text-[#E8E8E4]">Quota eredità</td>
                <td className="py-2.5 text-right text-[#E8E8E4]">€{fmt(quotaPerErede)}</td>
                <td className="py-2.5 text-right text-[#E8E8E4]">€{fmt(baseImponibile)}</td>
              </tr>
              <tr className="border-b border-white/[0.04]">
                <td className="py-2.5 text-[#888780]">Franchigia</td>
                <td className="py-2.5 text-right text-[#3D7A52]">- €{fmt(Math.min(cat.franchigia, quotaPerErede))}</td>
                <td className="py-2.5 text-right text-[#3D7A52]">- €{fmt(Math.min(cat.franchigia, quotaPerErede) * N)}</td>
              </tr>
              <tr className="border-b border-white/[0.04]">
                <td className="py-2.5 text-[#888780]">Imponibile dopo franchigia</td>
                <td className="py-2.5 text-right text-[#E8E8E4]">€{fmt(baseImponibileDopoFranchigia)}</td>
                <td className="py-2.5 text-right text-[#E8E8E4]">€{fmt(baseImponibileDopoFranchigia * N)}</td>
              </tr>
              <tr className="border-b border-white/[0.04]">
                <td className="py-2.5 text-[#E87040] font-medium">Imposta ({cat.aliquota * 100}%)</td>
                <td className="py-2.5 text-right text-[#E87040] font-medium">€{fmt(impostaSingoloErede)}</td>
                <td className="py-2.5 text-right text-[#E87040] font-medium">€{fmt(impostaTotale)}</td>
              </tr>
              <tr>
                <td className="py-2.5 text-[#3D7A52] font-semibold">Netto per erede</td>
                <td className="py-2.5 text-right text-[#3D7A52] font-semibold">€{fmt(nettoErede)}</td>
                <td className="py-2.5 text-right text-[#3D7A52] font-semibold">€{fmt(nettoErede * N)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODULO 2 — Quota di legittima ────────────────────────────── */}
      <AccordionSection
        title="Modulo 2 — Quota di legittima"
        subtitle="Art. 536-564 c.c."
        isOpen={open2}
        onToggle={() => setOpen2(v => !v)}
        testid="successione-modulo-2"
      >
        <p className="text-xs text-[#888780] mb-4 mt-2">
          La legge riserva quote minime ai legittimari (artt. 536-542 c.c.). Se il testamento le lede, i legittimari possono agire in riduzione (art. 553 c.c.) dopo l'apertura della successione. Gli ascendenti sono legittimari solo in assenza di figli. Le quote visualizzate tengono conto della composizione familiare indicata.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div>
            <Label className="text-[#888780] text-sm">Numero di figli</Label>
            <select
              value={numFigli}
              onChange={e => setNumFigli(e.target.value)}
              className="w-full mt-1 bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm"
              data-testid="mod2-num-figli"
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3 o più</option>
            </select>
          </div>
          <div>
            <Label className="text-[#888780] text-sm mb-2 block">Presenza coniuge</Label>
            <div className="flex gap-3" data-testid="mod2-coniuge">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="haConiuge" checked={haConiuge === true} onChange={() => setHaConiuge(true)} className="accent-[#C9A84C]" />
                <span className="text-sm text-[#E8E8E4]">Sì</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="haConiuge" checked={haConiuge === false} onChange={() => setHaConiuge(false)} className="accent-[#C9A84C]" />
                <span className="text-sm text-[#E8E8E4]">No</span>
              </label>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-testid="mod2-results">
          {quoteInEuro.legittimaConiuge != null && (
            <ResultCard
              label="Legittima coniuge"
              value={`€${fmt(quoteInEuro.legittimaConiuge)}`}
              color="#5B8DB8"
              sub={`${(quote.legittimaConiuge * 100).toFixed(1)}%`}
            />
          )}
          {quoteInEuro.legittimaFigli != null && (
            <ResultCard
              label={`Legittima figli (totale${nFigli > 0 ? `, ${nFigli}` : ''})`}
              value={`€${fmt(quoteInEuro.legittimaFigli)}`}
              color="#C9A84C"
              sub={nFigli > 0 ? `${(quote.legittimaFigli * 100).toFixed(1)}% · €${fmt(quoteInEuro.perFiglio)}/figlio` : `${(quote.legittimaFigli * 100).toFixed(1)}%`}
            />
          )}
          {quoteInEuro.legittima != null && (
            <ResultCard
              label="Legittima ascendenti"
              value={`€${fmt(quoteInEuro.legittima)}`}
              color="#5B8DB8"
              sub={`${(quote.legittima * 100).toFixed(1)}%`}
            />
          )}
          <ResultCard
            label="Quota disponibile"
            value={`€${fmt(quoteInEuro.disponibile)}`}
            color="#3D7A52"
            highlight
            sub={`${(quote.disponibile * 100).toFixed(1)}%`}
          />
        </div>

        <p
          className="text-xs text-[#888780] italic p-3 rounded-lg"
          style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}
          data-testid="mod2-note"
        >
          La <strong className="text-[#E8E8E4]">quota disponibile</strong> può essere destinata liberamente a chiunque tramite testamento.
          {quote.note && <> {quote.note}</>}
        </p>
      </AccordionSection>

      {/* ── MODULO 3 — Valore netto per erede ────────────────────────── */}
      <AccordionSection
        title="Modulo 3 — Valore netto per erede"
        subtitle="Composizione del patrimonio"
        isOpen={open3}
        onToggle={() => setOpen3(v => !v)}
        testid="successione-modulo-3"
      >
        <p className="text-xs text-[#888780] mb-4 mt-2">
          Indica la composizione del patrimonio per calcolare il valore fiscale tenendo conto della valutazione catastale degli immobili.
        </p>

        <div className="space-y-4 mb-5">
          {[
            { label: 'Immobili', value: percImmobili, setValue: setPercImmobili, color: '#5B8DB8', testid: 'mod3-slider-immobili' },
            { label: 'Liquidità', value: percLiquidita, setValue: setPercLiquidita, color: '#C9A84C', testid: 'mod3-slider-liquidita' },
            { label: 'Investimenti / altro', value: percInvestimenti, setValue: setPercInvestimenti, color: '#3D7A52', testid: 'mod3-slider-investimenti' },
          ].map(({ label, value, setValue, color, testid }) => (
            <div key={label}>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[#888780] text-sm">{label}</Label>
                <span className="text-sm font-medium" style={{ color }}>{value}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={value}
                onChange={e => setValue(parseInt(e.target.value, 10))}
                className="w-full"
                style={{ accentColor: color }}
                data-testid={testid}
              />
            </div>
          ))}
        </div>

        {needsNormalization && (
          <p className="text-xs text-[#E8903A] italic mb-3" data-testid="mod3-normalized">
            ⚠ Somma = {sumPerc}% · valori normalizzati proporzionalmente al 100% per il calcolo.
          </p>
        )}

        <div className="overflow-x-auto mb-4" data-testid="mod3-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 text-[#888780] font-medium">Erede</th>
                <th className="text-right py-2 text-[#888780] font-medium">Valore lordo</th>
                <th className="text-right py-2 text-[#888780] font-medium">Imposte</th>
                <th className="text-right py-2 text-[#888780] font-medium">Valore netto</th>
              </tr>
            </thead>
            <tbody>
              {eredi.map(r => (
                <tr key={r.idx} className="border-b border-white/[0.04]" data-testid={`mod3-row-${r.idx}`}>
                  <td className="py-2.5 text-[#E8E8E4]">Erede #{r.idx}</td>
                  <td className="py-2.5 text-right text-[#E8E8E4]">€{fmt(r.lordo)}</td>
                  <td className="py-2.5 text-right text-[#E87040]">€{fmt(r.imposte)}</td>
                  <td className="py-2.5 text-right text-[#3D7A52] font-semibold">€{fmt(r.netto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-[#888780] italic p-3 rounded-lg" style={{ background: 'rgba(91,141,184,0.05)', border: '1px solid rgba(91,141,184,0.15)' }}>
          Il valore fiscale degli immobili è stimato al <strong className="text-[#E8E8E4]">70% del valore di mercato</strong> come approssimazione del valore catastale. Il dato esatto dipende dalla rendita catastale dell'immobile.
        </p>
      </AccordionSection>

      {/* ── MODULO 4 — Confronto donazione in vita (Premium) ─────────── */}
      <AccordionSection
        title="Modulo 4 — Donazione in vita vs successione"
        subtitle="Pianificazione anticipata"
        isOpen={open4}
        onToggle={() => setOpen4(v => !v)}
        premium
        locked={!isPremium}
        testid="successione-modulo-4"
      >
        <p className="text-xs text-[#888780] mb-4 mt-2">
          Confronta l'imposta di successione futura con quella di una donazione anticipata dello stesso patrimonio.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-[#888780] text-sm">Anni stimati alla successione</Label>
              <span className="text-sm font-medium text-[#C9A84C]">{anniNum} anni</span>
            </div>
            <input
              type="range"
              min={1}
              max={30}
              step={1}
              value={anniNum}
              onChange={e => setAnniAllaSuccessione(parseInt(e.target.value, 10))}
              className="w-full"
              style={{ accentColor: '#C9A84C' }}
              data-testid="mod4-anni"
            />
          </div>
          <InputRow
            label="Tasso di crescita stimato del patrimonio (%)"
            value={tassoCrescita}
            onChange={setTassoCrescita}
          />
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <ResultCard
            label="Imposta se eredità (tra anni)"
            value={`€${fmt(impostaSuccessioneFutura)}`}
            color="#E87040"
            sub={`Su patrimonio futuro €${fmt(patrimonioFuturo)}`}
          />
          <ResultCard
            label="Imposta se donazione ora"
            value={`€${fmt(impostaDonazioneOra)}`}
            color="#C9A84C"
            sub={`Su patrimonio attuale €${fmt(V)}`}
          />
          <ResultCard
            label="Risparmio fiscale donazione"
            value={`€${fmt(risparmioFiscale)}`}
            color="#3D7A52"
            highlight
            sub={`+${risparmioPercentuale.toFixed(1)}%`}
          />
        </div>

        <div
          className="text-xs leading-relaxed p-3 rounded-lg mb-2"
          style={{ background: 'rgba(232,144,58,0.08)', border: '1px solid rgba(232,144,58,0.25)', color: '#E8903A' }}
          data-testid="mod4-warning"
        >
          ⚠️ Se il donante muore entro <strong>4 anni</strong> dalla donazione, le franchigie successorie si applicano in modo ridotto. Consulta un notaio per una valutazione precisa della tua situazione.
        </div>
{!isPremium && (
          <div className="mt-3 text-center relative" style={{ zIndex: 11 }}>
            <button
              onClick={() => navigate('/premium')}
              className="sapiens-btn-primary px-6 py-2.5 text-sm"
              data-testid="mod4-premium-cta"
            >
              Sblocca con Premium
            </button>
          </div>
        )}
      </AccordionSection>

      {/* ── Note informative generali (invariate) ─────────────────────── */}
      <div className="p-4 rounded-xl border text-sm text-[#888780] leading-relaxed mt-6" style={{ background: '#252523', borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="font-medium text-[#E8E8E4] mb-2">Note importanti</div>
        <ul className="space-y-1.5">
          <li>• I <strong className="text-[#E8E8E4]">BTP e titoli di Stato italiani</strong> sono esenti da imposta di successione</li>
          <li>• I <strong className="text-[#E8E8E4]">beneficiari di polizze vita</strong> ricevono il capitale fuori dall'asse ereditario (no imposta)</li>
          <li>• Gli <strong className="text-[#E8E8E4]">immobili</strong> vengono valutati al valore catastale (spesso inferiore al valore di mercato)</li>
          <li>• La franchigia di <strong className="text-[#E8E8E4]">€1.000.000</strong> è per beneficiario, non per eredità totale</li>
          <li>• Questo calcolatore è educativo. Per situazioni complesse consulta un notaio o un consulente fiscale</li>
        </ul>
      </div>
</motion.div>
  );
};

export default ImpostaSuccessioneCalc;
